/* Harber home: lazy videos (desktop autoplay in view, mobile tap-to-play) and autoplaying carousels. */
(() => {
  if (window.HarberHome) return;
  window.HarberHome = true;

  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Skip autoplay on Save-Data or anything slower than 4G (effectiveType is missing in Safari/Firefox: treat as fast).
  const conn = navigator.connection;
  const saveData = !!(conn && (conn.saveData || (conn.effectiveType && conn.effectiveType !== '4g')));
  window.HarberHomeSlow = saveData;
  const desktop = () => matchMedia('(min-width: 990px)').matches;

  const prepareForTap = (el) => {
    const tpl = el.querySelector('template');
    const node = tpl && tpl.content.firstElementChild;
    if (!node) return;
    if (node.tagName === 'VIDEO') {
      node.removeAttribute('muted');
      node.muted = false;
      node.removeAttribute('loop');
      node.setAttribute('controls', '');
    } else if (node.tagName === 'IFRAME') {
      node.src = node.src.replace('mute=1', 'mute=0').replace('controls=0', 'controls=1').replace('muted=1', 'muted=0').replace('background=1', 'background=0');
    }
    el.addEventListener('click', () => el.classList.add('is-tapped'), { once: true });
  };

  const initVideo = (el) => {
    if (el.dataset.homeVideoReady || !el.hasAttribute('data-has-media')) return;
    el.dataset.homeVideoReady = '1';
    if (!desktop() || reduced || saveData) return prepareForTap(el);
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (!el.loaded) el.load();
          el.querySelector('video')?.play?.().catch(() => {});
        } else {
          el.querySelector('video')?.pause?.();
        }
      });
    }, { rootMargin: '150px 0px' });
    io.observe(el);
  };

  const initCarousel = (root) => {
    if (root.dataset.autoplayReady || reduced) return;
    root.dataset.autoplayReady = '1';
    const track = root.querySelector('[data-track]');
    if (!track) return;
    const speed = Math.max(3, Number(root.dataset.autoplay) || 5) * 1000;
    let paused = false;
    let visible = false;
    ['mouseenter', 'focusin', 'touchstart'].forEach((e) => root.addEventListener(e, () => (paused = true), { passive: true }));
    ['mouseleave', 'focusout'].forEach((e) => root.addEventListener(e, () => (paused = false)));
    new IntersectionObserver((entries) => (visible = entries.some((x) => x.isIntersecting))).observe(root);
    setInterval(() => {
      if (paused || !visible || document.hidden) return;
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      track.scrollTo({ left: atEnd ? 0 : track.scrollLeft + track.clientWidth, behavior: 'smooth' });
    }, speed);
  };

  const init = (scope = document) => {
    customElements.whenDefined('deferred-media').then(() => scope.querySelectorAll('[data-home-video]').forEach(initVideo));
    scope.querySelectorAll('[data-autoplay]').forEach(initCarousel);
  };

  init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();

if (!customElements.get('hero-panels')) {
  if (!customElements.get('hero-panels')) customElements.define('hero-panels', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.dots = [...this.querySelectorAll('[data-dot]')];
      this.dots.forEach((d) => d.addEventListener('click', () => this.go(Number(d.dataset.dot), true)));
      this.track.addEventListener('scroll', () => { clearTimeout(this.t); this.t = setTimeout(() => this.sync(), 80); }, { passive: true });
      const slow = !!window.HarberHomeSlow || matchMedia('(prefers-reduced-motion: reduce)').matches;
      // Panel videos (muted loops) start only after the page has loaded, so they don't compete with the LCP image for bandwidth.
      const loaded = document.readyState === 'complete' ? Promise.resolve() : new Promise((r) => addEventListener('load', r, { once: true }));
      const io = new IntersectionObserver((es) => es.forEach((e) => {
        const v = e.target.querySelector('video');
        if (!v || slow) return;
        v._inView = e.isIntersecting;
        if (e.isIntersecting) loaded.then(() => { if (v._inView) v.play?.().catch(() => {}); });
        else v.pause?.();
      }), { threshold: 0.4 });
      this.slides.forEach((s) => io.observe(s));
      const secs = Number(this.dataset.interval || 0);
      const sliding = () => getComputedStyle(this.track).overflowX !== 'visible';
      if (secs > 0 && this.slides.length > 1 && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        /* The slideshow starts on the visitor's first scroll / tap / key, not on its own: an unattended slide change
         * would replace the first image as the page's largest paint (LCP) and fetch the next slide during load. */
        const start = () => {
          ['scroll', 'keydown', 'pointerdown', 'touchstart'].forEach((ev) => removeEventListener(ev, start, true));
          if (this.timer || this.stopped) return;
          this.timer = setInterval(() => { if (sliding() && !this.matches(':hover, :focus-within')) this.go((this.index + 1) % this.slides.length); }, secs * 1000);
        };
        ['scroll', 'keydown', 'pointerdown', 'touchstart'].forEach((ev) => addEventListener(ev, start, { capture: true, passive: true }));
        // A manual swipe or tap on the hero means the visitor is in control: stop advancing.
        this.track.addEventListener('pointerdown', () => { this.stopped = true; clearInterval(this.timer); }, { once: true, passive: true });
      }
      this.index = 0;
      this.mark(0);
    }
    /* Slides in on-screen order: CSS can move one first on phones (.hero-panel--m-first) */
    get ordered() { return [...this.slides].sort((a, b) => a.offsetLeft - b.offsetLeft); }
    disconnectedCallback() { clearInterval(this.timer); }
    go(i, user) { this.index = i; this.track.scrollTo({ left: this.ordered[i].offsetLeft, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }); this.mark(i); if (user) clearInterval(this.timer); }
    sync() { const i = Math.round(this.track.scrollLeft / (this.track.clientWidth || 1)); this.index = Math.min(i, this.slides.length - 1); this.mark(this.index); }
    mark(i) { this.ordered.forEach((s, n) => s.classList.toggle('is-active', n === i)); this.dots.forEach((d, n) => d.classList.toggle('is-active', n === i)); }
  });
}
